// src/pages/About/About.js
import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';
import { useSettings } from '../../hooks/useSettings';

function About() {
  const { settings } = useSettings();

  return (
    <div className="about-page page-container">
      <div className="about-header page-header">
        <h1>About Me</h1>
      </div>

      <div className="about-container page-container-card">
        <div className="about-intro">
          <div className="about-portrait">
            {settings.profileImageUrl && (
              <img
                src={settings.profileImageUrl}
                alt="Carl Gathmann"
                className="portrait-image"
              />
            )}
          </div>
          <div className="about-intro-text">
            <h2>Carl Gathmann</h2>
            <h3>Robotics & Autonomous Systems Engineer</h3>
            <p>
              I'm a passionate engineer specializing in robotics and autonomous systems, currently pursuing my Master's degree at the University of Lübeck. With a strong foundation in both hardware and software development, I create elegant solutions for complex challenges in the field of robotics.
            </p>
            <div className="about-buttons">
              <Link to="/projects" className="primary-button btn-primary">View My Projects</Link>
              <Link to="/contact" className="secondary-button btn-secondary">Get In Touch</Link>
            </div>
          </div>
        </div>

        <div className="about-section page-section">
          <h2 className="section-title title-line line-blue">Background</h2>
          <div className="about-content">
            <p>
              My journey in robotics began early with LEGO Technic projects, including a brick sorter
              I built in 2014. This early passion led me to pursue formal education in Robotics and
              Autonomous Systems at the University of Lübeck, where I've been developing my skills
              since 2019.
            </p>
            <p>
              I've gained valuable professional experience through my work as a Scientific Assistant
              at Fraunhofer IMTE, where I focused on state-of-the-art medical robots and tracking systems.
              This experience, combined with my academic background, has given me a comprehensive
              understanding of both the theoretical and practical aspects of robotics engineering.
            </p>
            <p>
              Throughout my academic and professional journey, I've maintained a hands-on approach to
              learning, consistently working on projects that challenge me to apply my knowledge in
              real-world scenarios.
            </p>
          </div>
        </div>

        <div className="about-section page-section">
          <h2 className="section-title title-line line-warm">Technical Skills</h2>
          <div className="skills-container">
            <div className="skill-category">
              <h3>Programming Languages</h3>
              <ul className="skill-list">
                <li>Java</li>
                <li>Python</li>
                <li>C++</li>
                <li>JavaScript/React</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Robotics</h3>
              <ul className="skill-list">
                <li>ROS (Robot Operating System)</li>
                <li>Robot Kinematics</li>
                <li>Control Systems</li>
                <li>Sensor Integration</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Design & Simulation</h3>
              <ul className="skill-list">
                <li>Fusion 360</li>
                <li>AutoCAD</li>
                <li>3D Printing</li>
                <li>Simulation Tools</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="about-section page-section">
          <h2 className="section-title title-line line-cool">Professional Interests</h2>
          <div className="about-content">
            <p>
              At my core, I'm a tinkerer who loves building things that work. Whether it's hardware
              or software, I get the most satisfaction from taking an idea and turning it into
              something functional. My interests include:
            </p>
            <ul className="interests-list">
              <li>Home automation and smart home systems — building DIY solutions to automate everyday tasks</li>
              <li>Hardware prototyping — from PCB design to 3D printing custom enclosures</li>
              <li>IoT projects that bridge the gap between physical devices and software</li>
              <li>Building robots and autonomous systems that can interact with the real world</li>
              <li>Writing clean, efficient code that makes complex systems feel simple</li>
            </ul>
            <p>
              What drives me most is the joy of creating — whether it's a smart home sensor,
              a custom automation script, or a full robotic system.
            </p>
          </div>
        </div>

        <div className="about-cta">
          <h2 className="title-line line-primary centered">Let's Connect</h2>
          <p>
            I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
          </p>
          <Link to="/contact" className="primary-button btn-primary">Contact Me</Link>
        </div>
      </div>
    </div>
  );
}

export default About;